import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send, Phone, Mail, User } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Welkom bij Kine Julien Ryckeboer! 👋\n\nHoe kan ik u helpen? Ik kan u informeren over onze diensten of u helpen bij het aanvragen van een afspraak.\n\n📞 Bel direct: 0476 51 08 89'
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId] = useState(() => `chat_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactInfo, setContactInfo] = useState({ name: '', phone: '', reason: '' });
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;

    const userMessage = inputMessage.trim();
    setInputMessage('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId
        })
      });

      const data = await response.json();
      setMessages(prev => [...prev, { role: 'assistant', content: data.response }]);

      // Check for contact info in the conversation
      const lowerMessage = userMessage.toLowerCase();
      const lowerResponse = data.response.toLowerCase();
      
      // Try to extract visitor info from messages
      extractVisitorInfo(userMessage);
      
      // Auto-send when appointment is being discussed
      if (lowerMessage.includes('afspraak') || lowerMessage.includes('contact') || 
          lowerResponse.includes('bevestiging') || lowerResponse.includes('aanvraag')) {
        sendConversationToEmail(true);
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, er is iets misgegaan. U kunt ons bereiken via telefoon: 0476 51 08 89'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Extract visitor info from messages
  const extractVisitorInfo = (message) => {
    const info = { ...contactInfo };
    
    // Try to extract phone number
    const phoneMatch = message.match(/(\+32|0)\s*[0-9\s]{8,}/);
    if (phoneMatch) {
      info.telefoon = phoneMatch[0].replace(/\s/g, '');
    }
    
    // Try to extract email
    const emailMatch = message.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
    if (emailMatch) {
      info.email = emailMatch[0];
    }
    
    // Update if we found something
    if (info.telefoon || info.email) {
      setContactInfo(info);
    }
  };

  const sendConversationToEmail = async (contactRequested = false) => {
    // Build visitor info from conversation
    const allMessages = messages.map(m => m.content).join(' ');
    const visitorInfo = { ...contactInfo };
    
    // Extract any info we might have missed
    const phoneMatch = allMessages.match(/(\+32|0)\s*[0-9\s]{8,}/);
    if (phoneMatch) visitorInfo.telefoon = phoneMatch[0].replace(/\s/g, '');
    
    const emailMatch = allMessages.match(/[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/);
    if (emailMatch) visitorInfo.email = emailMatch[0];
    
    // Try to get name (first capitalized words after common patterns)
    const namePatterns = [
      /(?:naam is|ik ben|mijn naam is|dit is)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i,
      /^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?),/
    ];
    for (const pattern of namePatterns) {
      const nameMatch = allMessages.match(pattern);
      if (nameMatch) {
        visitorInfo.naam = nameMatch[1];
        break;
      }
    }
    
    // Extract reason/complaint
    const redenPatterns = [
      /(?:last van|klacht|probleem met|pijn aan|voor)\s+(.+?)(?:\.|,|$)/i
    ];
    for (const pattern of redenPatterns) {
      const redenMatch = allMessages.match(pattern);
      if (redenMatch) {
        visitorInfo.reden = redenMatch[1];
        break;
      }
    }
    
    try {
      await fetch(`${BACKEND_URL}/api/chat/send-conversation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          conversation: messages,
          contact_requested: contactRequested,
          visitor_info: Object.keys(visitorInfo).length > 0 ? visitorInfo : null
        })
      });
    } catch (error) {
      console.error('Error sending conversation:', error);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleClose = () => {
    // Send conversation when closing
    if (messages.length > 1) {
      sendConversationToEmail(false);
    }
    setIsOpen(false);
  };

  const quickActions = [
    { label: '📞 Bel voor afspraak', action: () => window.location.href = 'tel:+32476510889' },
    { label: '📍 Waar is de praktijk?', action: () => setInputMessage('Waar is de praktijk gelegen?') },
    { label: '🏥 Welke behandelingen?', action: () => setInputMessage('Welke behandelingen bieden jullie aan?') },
  ];

  return (
    <>
      {/* Chat Button - positioned to cover Emergent badge */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-[8px] right-[8px] z-[99999] bg-primary-green hover:bg-primary-green-dark text-white px-8 py-4 rounded-full shadow-xl hover:scale-105 transition-all duration-300 ${isOpen ? 'hidden' : 'flex'} items-center gap-2 min-w-[200px] justify-center text-lg`}
      >
        <MessageCircle className="h-6 w-6" />
        <span className="hidden sm:inline font-medium">Start Chat gesprek</span>
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-16 right-4 z-[9999] w-[380px] max-w-[calc(100vw-2rem)] h-[500px] max-h-[calc(100vh-6rem)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-green to-primary-blue p-4 text-white flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <MessageCircle className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold">Kine Julien Ryckeboer</h3>
                <p className="text-xs text-white/80">Online assistent</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, index) => (
              <div
                key={index}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div
                  className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-primary-blue text-white rounded-br-md'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex justify-start">
                <div className="bg-white p-3 rounded-2xl rounded-bl-md shadow-sm border border-gray-100">
                  <div className="flex gap-1">
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></span>
                    <span className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length <= 2 && (
            <div className="px-4 py-2 border-t border-gray-100 bg-white">
              <div className="flex flex-wrap gap-2">
                {quickActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      action.action();
                      if (action.label.includes('Bel')) return;
                      setTimeout(() => sendMessage(), 100);
                    }}
                    className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded-full transition-colors"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 border-t border-gray-100 bg-white">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Stel uw vraag..."
                className="flex-1 px-4 py-2 border border-gray-200 rounded-full focus:outline-none focus:border-primary-green focus:ring-1 focus:ring-primary-green text-sm"
                disabled={isLoading}
              />
              <button
                onClick={sendMessage}
                disabled={isLoading || !inputMessage.trim()}
                className="bg-primary-green hover:bg-primary-green-dark disabled:bg-gray-300 text-white p-2 rounded-full transition-colors"
              >
                <Send className="h-5 w-5" />
              </button>
            </div>
            <p className="text-xs text-gray-400 text-center mt-2">
              Direct bellen? <a href="tel:+32476510889" className="text-primary-blue hover:underline">0476 51 08 89</a>
            </p>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
